import React, { useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import { Navigate, useLocation } from 'react-router-dom';
import { BugFill, ChatSquareTextFill, LightbulbFill } from 'react-bootstrap-icons';

import { submitFeedback } from '../api/feedback';
import { useAuth } from '../contexts/AuthContext';
import './Feedback-new.css';

type FeedbackCategory = 'feedback' | 'bug' | 'suggestion';

function FeedbackPage(): React.ReactElement
{
    const { user } = useAuth();
    const location = useLocation();
    const [category, setCategory] = useState<FeedbackCategory>('feedback');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const categoryOptions = useMemo(() => ([
        { value: 'feedback' as const, label: 'General Feedback', icon: ChatSquareTextFill },
        { value: 'bug' as const, label: 'Report a Bug', icon: BugFill },
        { value: 'suggestion' as const, label: 'Suggestion', icon: LightbulbFill },
    ]), []);

    const userRole = useMemo(() =>
    {
        const normalizedRole = String(user?.role || '').trim().toLowerCase();
        if (normalizedRole === 'superadmin')
        {
            return 'Super Admin';
        }
        const normalizedOrgRole = String(user?.organization_role || '').trim().toLowerCase();
        if (normalizedOrgRole === 'admin')
        {
            return 'Organization Admin';
        }
        return 'User';
    }, [user?.organization_role, user?.role]);

    if (String(user?.role || '').trim().toLowerCase() === 'superadmin')
    {
        return <Navigate to="/admin/feedback" replace />;
    }

    async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void>
    {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (subject.trim().length < 3)
        {
            setError('Please add a short subject so we can triage this properly.');
            return;
        }
        if (message.trim().length < 10)
        {
            setError('Please add a bit more detail before submitting.');
            return;
        }

        setSaving(true);
        try
        {
            const response = await submitFeedback({
                category,
                subject: subject.trim(),
                message: message.trim(),
                page_url: location.pathname,
            });
            setSuccess(response.message || 'Feedback submitted successfully.');
            setSubject('');
            setMessage('');
            setCategory('feedback');
        }
        catch (err: any)
        {
            setError(err?.response?.data?.detail || err?.message || 'Unable to submit feedback right now.');
        }
        finally
        {
            setSaving(false);
        }
    }

    return (
        <div className="feedback-page">
            <section className="feedback-hero">
                <div className="feedback-hero-copy">
                    <span className="feedback-eyebrow">Feedback</span>
                    <h1 className="feedback-title">Help us improve AcceliQA</h1>
                    <p className="feedback-summary">
                        Share product feedback, report issues, or suggest improvements. We will attach your account context automatically.
                    </p>
                </div>
                <div className="feedback-identity">
                    <div className="feedback-identity-label">Submitting as</div>
                    <div className="feedback-identity-name">{user?.name || 'Signed-in user'}</div>
                    <div className="feedback-identity-meta">
                        <span>{user?.email}</span>
                        <span>{userRole}</span>
                        {user?.organization_name && <span>{user.organization_name}</span>}
                    </div>
                </div>
            </section>

            <section className="feedback-form-shell">
                <form className="feedback-form" onSubmit={handleSubmit}>
                    <div className="feedback-section">
                        <h2 className="feedback-section-title">What kind of feedback is this?</h2>
                        <div className="feedback-category-grid" role="radiogroup" aria-label="Feedback category">
                            {categoryOptions.map((option) =>
                            {
                                const Icon = option.icon;
                                const active = category === option.value;
                                return (
                                    <button
                                        key={option.value}
                                        type="button"
                                        className={`feedback-category-btn${active ? ' is-active' : ''}`}
                                        onClick={() => setCategory(option.value)}
                                        aria-pressed={active}
                                    >
                                        <Icon className="feedback-category-icon" />
                                        <span>{option.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="feedback-section">
                        <label className="feedback-label" htmlFor="feedback-subject">Subject</label>
                        <input
                            id="feedback-subject"
                            className="feedback-input"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="Short summary of the issue or idea"
                            maxLength={160}
                            disabled={saving}
                        />
                    </div>

                    <div className="feedback-section">
                        <label className="feedback-label" htmlFor="feedback-message">Details</label>
                        <textarea
                            id="feedback-message"
                            className="feedback-textarea"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="What happened, what you expected, or what would make this workflow better."
                            maxLength={4000}
                            disabled={saving}
                            rows={10}
                        />
                    </div>

                    {error && <Alert variant="danger" className="mb-0">{error}</Alert>}
                    {success && <Alert variant="success" className="mb-0">{success}</Alert>}

                    <div className="feedback-actions">
                        <button type="submit" className="feedback-submit-btn" disabled={saving}>
                            {saving && <Spinner animation="border" size="sm" className="feedback-submit-spinner" />}
                            <span>{saving ? 'Sending' : 'Send Feedback'}</span>
                        </button>
                    </div>
                </form>
            </section>
        </div>
    );
}

export default FeedbackPage;
